import { Backdrop, CircularProgress, Dialog } from "@mui/material";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { hasChainProfile } from "@/helper/contractFunctions/MafiaProfile";
import useWallet from "@/hook/useWallet";
import { hasSavedTermsAgreement, loadTermsDocument, type TermsDocument } from "./termsAgreement";
import TermsModalContent from "./TermsModalContent";

type AgreementStatus = "loading" | "required" | "accepted" | "error";
type AccessStatus = AgreementStatus | "browsing";
type ProfileStatus = "loading" | "exists" | "missing" | "error";
type TermsDialogContextValue = { accepted: boolean; openTerms: () => void };
const TermsDialogContext = createContext<TermsDialogContextValue>({ accepted: false, openTerms: () => {} });
export const useTermsDialog = () => useContext(TermsDialogContext);

const TermsAccess = ({ children }: React.PropsWithChildren) => {
  const { account } = useWallet();
  const { pathname } = useLocation();
  const isProfileCreationRoute = pathname === "/create-profile" || pathname === "/profile-unboxing";
  const [terms, setTerms] = useState<TermsDocument | null>(null);
  const [loadError, setLoadError] = useState("");
  const [profileResult, setProfileResult] = useState<{ account: string; status: ProfileStatus; error: string }>({ account: "", status: "loading", error: "" });
  const [result, setResult] = useState<{ account: string; hash: string; status: AgreementStatus; error: string }>({ account: "", hash: "", status: "loading", error: "" });
  const [retry, setRetry] = useState(0);
  const [manualOpen, setManualOpen] = useState(false);
  const gameRef = useRef<HTMLDivElement>(null);
  const profileResultMatches = profileResult.account.toLowerCase() === account.toLowerCase();
  const resultMatches = !!terms && result.account.toLowerCase() === account.toLowerCase() && result.hash === terms.hash;
  const status: AccessStatus = !account || isProfileCreationRoute ? "browsing"
    : !profileResultMatches || profileResult.status === "loading" ? "loading"
    : profileResult.status === "missing" ? "browsing"
    : profileResult.status === "error" || loadError ? "error"
    : !terms ? "loading" : resultMatches ? result.status : "loading";
  const visibleError = (profileResultMatches ? profileResult.error : "") || loadError || (resultMatches ? result.error : "");
  const canBrowse = status === "accepted" || status === "browsing";
  const modalOpen = manualOpen || status === "required" || status === "error";
  const context = useMemo(() => ({ accepted: canBrowse, openTerms: () => setManualOpen(true) }), [canBrowse]);

  useEffect(() => {
    let active = true;
    setTerms(null);
    setLoadError("");
    loadTermsDocument()
      .then((document) => { if (active) setTerms(document); })
      .catch((cause) => { if (active) setLoadError(cause instanceof Error ? cause.message : "Could not load the terms"); });
    return () => { active = false; };
  }, [retry]);

  useEffect(() => {
    if (!account || isProfileCreationRoute) {
      setProfileResult({ account, status: "loading", error: "" });
      return;
    }
    let active = true;
    setProfileResult({ account, status: "loading", error: "" });
    hasChainProfile(account)
      .then((exists) => { if (active) setProfileResult({ account, status: exists ? "exists" : "missing", error: "" }); })
      .catch((cause) => { if (active) setProfileResult({ account, status: "error", error: cause instanceof Error ? cause.message : "Could not check your profile" }); });
    return () => { active = false; };
  }, [account, isProfileCreationRoute, retry]);

  useEffect(() => {
    if (!account || !terms || !profileResultMatches || profileResult.status !== "exists" || isProfileCreationRoute) return;
    let active = true;
    setResult({ account, hash: terms.hash, status: "loading", error: "" });
    hasSavedTermsAgreement(terms, account)
      .then((found) => { if (active) setResult({ account, hash: terms.hash, status: found ? "accepted" : "required", error: "" }); })
      .catch((cause) => { if (active) setResult({ account, hash: terms.hash, status: "error", error: cause instanceof Error ? cause.message : "Could not check your saved agreement" }); });
    return () => { active = false; };
  }, [account, terms, profileResultMatches, profileResult.status, isProfileCreationRoute]);

  useEffect(() => {
    if (gameRef.current) gameRef.current.inert = !canBrowse || manualOpen;
  }, [canBrowse, manualOpen]);

  return (
    <TermsDialogContext.Provider value={context}>
      <div data-testid="game-content" ref={gameRef} aria-hidden={!canBrowse || manualOpen} style={{ pointerEvents: canBrowse && !manualOpen ? undefined : "none" }}>
        {children}
      </div>
      <Backdrop open={status === "loading" && !manualOpen} sx={(theme) => ({ zIndex: theme.zIndex.modal, color: theme.design.color.action.primary })}>
        <CircularProgress aria-label="Checking Terms of Service agreement" color="inherit" />
      </Backdrop>
      <Dialog
        open={modalOpen}
        onClose={() => { if (canBrowse) setManualOpen(false); }}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        disableEscapeKeyDown={!canBrowse}
        PaperProps={{ sx: (theme) => ({ bgcolor: theme.design.color.surface.raised, color: theme.design.color.text.primary, height: "min(90vh, 920px)", maxHeight: "90vh", width: "min(1100px, calc(100% - 32px))", m: { xs: 1, sm: 2 }, borderRadius: `${theme.design.radius.lg}px`, border: `1px solid ${theme.design.color.border.popover}`, boxShadow: theme.design.shadow.lg, overflow: "hidden" }) }}
      >
        {modalOpen && (
          <TermsModalContent
            account={account}
            status={status === "browsing" ? "accepted" : status}
            terms={terms}
            loadError={visibleError}
            onAccepted={() => setResult({ account, hash: terms?.hash ?? "", status: "accepted", error: "" })}
            onClose={() => setManualOpen(false)}
            onRetry={() => setRetry((value) => value + 1)}
          />
        )}
      </Dialog>
    </TermsDialogContext.Provider>
  );
};

export default TermsAccess;
